import React, { useState, useEffect } from 'react';
import api from '../../api/auth';

const TeacherAttendance = () => {
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [pendingJustifications, setPendingJustifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [selectedClass, setSelectedClass] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [time, setTime] = useState('08:00');
  const [attendance, setAttendance] = useState({});

  useEffect(() => {
    fetchClasses();
    fetchPendingJustifications();
  }, []);

  const fetchClasses = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get('/teacher/classes', { headers: { Authorization: `Bearer ${token}` } });
      setClasses(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingJustifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get('/teacher/absences/pending', { headers: { Authorization: `Bearer ${token}` } });
      setPendingJustifications(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Erreur justifications:', err);
    }
  };

  const loadStudents = async (classId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get(`/teacher/classes/${classId}/students`, { headers: { Authorization: `Bearer ${token}` } });
      const studentsList = Array.isArray(res.data) ? res.data : [];
      setStudents(studentsList);
      const initial = {};
      studentsList.forEach((s) => { initial[s.id] = { status: 'present', reason: '' }; });
      setAttendance(initial);
    } catch (err) {
      console.error('Erreur:', err);
      setStudents([]);
    }
  };

  const handleClassChange = (e) => {
    const classId = e.target.value;
    setSelectedClass(classId);
    if (classId) loadStudents(classId);
    else setStudents([]);
  };

  const handleStatusChange = (studentId, status) => {
    setAttendance((prev) => ({ ...prev, [studentId]: { ...prev[studentId], status, reason: status === 'present' ? '' : prev[studentId]?.reason || '' } }));
  };

  const handleReasonChange = (studentId, reason) => {
    setAttendance((prev) => ({ ...prev, [studentId]: { ...prev[studentId], reason } }));
  };

  const handleSave = async () => {
    if (!selectedClass) { alert('Veuillez sélectionner une classe'); return; }
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const attendancePayload = students.map((s) => ({
        student_id: s.id,
        status: attendance[s.id]?.status || 'present',
        reason: attendance[s.id]?.reason || '',
      }));
      const res = await api.post('/teacher/attendance', {
        class_id: selectedClass, date, time, attendance: attendancePayload,
      }, { headers: { Authorization: `Bearer ${token}` } });
      alert(`✅ Appel enregistré ! ${res.data.absences_created} absence(s) créée(s)`);
      const initial = {};
      students.forEach((s) => { initial[s.id] = { status: 'present', reason: '' }; });
      setAttendance(initial);
    } catch (err) {
      alert('❌ Erreur : ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (id) => {
    if (!window.confirm('Approuver cette justification ?')) return;
    try {
      const token = localStorage.getItem('token');
      await api.post(`/teacher/absences/${id}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      alert('✅ Justification approuvée');
      fetchPendingJustifications();
    } catch (err) {
      alert('❌ Erreur : ' + (err.response?.data?.error || err.message));
    }
  };

  const handleReject = async (id) => {
    const reason = window.prompt('Motif du refus :', '');
    if (reason === null) return;
    try {
      const token = localStorage.getItem('token');
      await api.post(`/teacher/absences/${id}/reject`, { reason }, { headers: { Authorization: `Bearer ${token}` } });
      alert('❌ Justification refusée');
      fetchPendingJustifications();
    } catch (err) {
      alert('❌ Erreur : ' + (err.response?.data?.error || err.message));
    }
  };

  const stats = () => {
    let present = 0, absent = 0, late = 0, renvoye = 0;
    students.forEach((s) => {
      const status = attendance[s.id]?.status || 'present';
      if (status === 'present') present++;
      else if (status === 'absent') absent++;
      else if (status === 'late') late++;
      else if (status === 'renvoye') renvoye++; // ⬅️ AJOUT
    });
    return { present, absent, late, renvoye }; // ⬅️ AJOUT
  };

  if (loading) return <div className="p-6">Chargement...</div>;
  const { present, absent, late, renvoye } = stats(); // ⬅️ AJOUT

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#2C3E50]">📋 Faire l'appel</h1>
        <p className="text-gray-500">Enregistrez les présences et absences</p>
      </div>

      {/* DEMANDES EN ATTENTE */}
      {pendingJustifications.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-5">
          <h3 className="font-bold text-orange-800 mb-3">📨 Demandes de justification à valider ({pendingJustifications.length})</h3>
          <div className="space-y-3">
            {pendingJustifications.map((p) => (
              <div key={p.id} className="bg-white rounded-lg p-4 flex items-center justify-between flex-wrap gap-3">
                <div className="flex-1 min-w-[200px]">
                  <p className="font-bold text-[#2C3E50]">{p.student_name} <span className="text-xs text-gray-500">({p.class_name})</span></p>
                  <p className="text-sm text-gray-500">Le {p.date} — {p.type === 'absence' ? '🚨 Absence' : '⏰ Retard'}</p>
                  <p className="text-sm text-gray-700 mt-1"><strong>Motif :</strong> {p.reason}</p>
                </div>
                <div className="flex items-center gap-2">
                  {p.justification_file && (
                    <a href={p.justification_file} target="_blank" rel="noreferrer" className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-100">
                      📄 Voir justificatif
                    </a>
                  )}
                  <button onClick={() => handleApprove(p.id)} className="px-3 py-1.5 bg-green-500 text-white rounded-lg text-xs font-medium hover:bg-green-600">✅ Approuver</button>
                  <button onClick={() => handleReject(p.id)} className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-xs font-medium hover:bg-red-600">❌ Refuser</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-md p-6">
        <h3 className="font-bold text-[#2C3E50] mb-4">📋 Informations de l'appel</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Classe *</label>
            <select value={selectedClass} onChange={handleClassChange} className="w-full px-3 py-2 border rounded-lg">
              <option value="">-- Choisir --</option>
              {classes.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Date *</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Heure</label>
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
          </div>
        </div>
      </div>

      {students.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl shadow-md p-4 text-center border-l-4 border-green-500">
            <p className="text-sm text-gray-500">Présents</p>
            <p className="text-2xl font-bold text-green-600">{present}</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-4 text-center border-l-4 border-red-500">
            <p className="text-sm text-gray-500">Absents</p>
            <p className="text-2xl font-bold text-red-600">{absent}</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-4 text-center border-l-4 border-yellow-500">
            <p className="text-sm text-gray-500">Retards</p>
            <p className="text-2xl font-bold text-yellow-600">{late}</p>
          </div>
          {/* ⬇️ AJOUT STAT RENVOYÉ ⬇️ */}
          <div className="bg-white rounded-xl shadow-md p-4 text-center border-l-4 border-purple-500">
            <p className="text-sm text-gray-500">Renvoyés</p>
            <p className="text-2xl font-bold text-purple-600">{renvoye}</p>
          </div>
        </div>
      )}

      {students.length > 0 && (
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-bold text-[#2C3E50]">👨‍🎓 Élèves ({students.length})</h3>
          </div>
          <div className="divide-y">
            {students.map((student) => {
              const status = attendance[student.id]?.status || 'present';
              return (
                <div key={student.id} className="p-4">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center space-x-3">
                      {/* ⬇️ AJOUT COULEUR VIOLETTE ⬇️ */}
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${status === 'present' ? 'bg-green-500' : status === 'absent' ? 'bg-red-500' : status === 'renvoye' ? 'bg-purple-500' : 'bg-yellow-500'}`}>
                        {student.name?.charAt(0) || '?'}
                      </div>
                      <div>
                        <p className="font-semibold text-[#2C3E50]">{student.name}</p>
                        <p className="text-xs text-gray-500">{student.matricule || ''}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 flex-wrap">
                      <button onClick={() => handleStatusChange(student.id, 'present')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${status === 'present' ? 'bg-green-500 text-white' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}>🟢 Présent</button>
                      <button onClick={() => handleStatusChange(student.id, 'absent')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${status === 'absent' ? 'bg-red-500 text-white' : 'bg-red-50 text-red-700 hover:bg-red-100'}`}>🔴 Absent</button>
                      <button onClick={() => handleStatusChange(student.id, 'late')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${status === 'late' ? 'bg-yellow-500 text-white' : 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100'}`}>🟡 Retard</button>
                      {/* ⬇️ AJOUT BOUTON RENVOYÉ ⬇️ */}
                      <button onClick={() => handleStatusChange(student.id, 'renvoye')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${status === 'renvoye' ? 'bg-purple-500 text-white' : 'bg-purple-50 text-purple-700 hover:bg-purple-100'}`}>🚪 Renvoyé</button>
                    </div>
                  </div>
                  {/* ⬇️ AJOUT MOTIF POUR RENVOYÉ ⬇️ */}
                  {(status === 'absent' || status === 'late' || status === 'renvoye') && (
                    <div className="mt-3">
                      <input type="text" value={attendance[student.id]?.reason || ''} onChange={(e) => handleReasonChange(student.id, e.target.value)} placeholder={status === 'renvoye' ? "Motif du renvoi (ex: perturbation)" : "Motif (optionnel)"} className="w-full md:w-1/2 px-3 py-1.5 text-sm border rounded-lg" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="p-4 bg-gray-50 flex justify-end">
            <button onClick={handleSave} disabled={saving} className="px-6 py-2 bg-[#E67E22] text-white rounded-lg hover:bg-[#D35400] transition disabled:opacity-50">
              {saving ? 'Enregistrement...' : '💾 Enregistrer l\'appel'}
            </button>
          </div>
        </div>
      )}

      {students.length === 0 && !selectedClass && (
        <div className="bg-white rounded-xl shadow-md p-8 text-center text-gray-500">
          <p className="text-lg mb-2">📋 Sélectionnez une classe pour commencer</p>
        </div>
      )}
    </div>
  );
};

export default TeacherAttendance;